import { motion } from "framer-motion";
import { useLang } from "../i18n/i18n";

const W = 640, H = 300, PAD = { l: 16, r: 16, t: 30, b: 34 };

function buildPath(points) {
  const vals = points.map((p) => p.value);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => PAD.l + (i / (points.length - 1)) * (W - PAD.l - PAD.r);
  const y = (v) => PAD.t + (1 - (v - min) / span) * (H - PAD.t - PAD.b);
  const pts = points.map((p, i) => [x(i), y(p.value)]);
  let d = `M ${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2[0].toFixed(1)},${p2[1].toFixed(1)}`;
  }
  const area = `${d} L ${pts[pts.length - 1][0].toFixed(1)},${H - PAD.b} L ${pts[0][0].toFixed(1)},${H - PAD.b} Z`;
  return { line: d, area, pts };
}

export default function GoldChart({ points }) {
  const { lang } = useLang();
  if (!points || points.length < 2) return null;
  const { line, area, pts } = buildPath(points);
  const fmt = (v) =>
    lang === "en"
      ? `$${Math.round(v).toLocaleString("en-CA")}`
      : `${Math.round(v).toLocaleString("fr-CA")} $`;
  const first = points[0];
  const last = points[points.length - 1];
  const idxs = [...new Set([...points.map((_, i) => i).filter((i) => i % 4 === 0), points.length - 1])];

  return (
    <div className="rounded-2xl border border-gold/15 bg-panel/60 p-5 sm:p-8" data-testid="gold-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="gold-chart-svg" role="img" aria-label="Gold price history">
        <defs>
          <linearGradient id="gc-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#8C6D1F" />
            <stop offset="0.5" stopColor="#F0D68A" />
            <stop offset="1" stopColor="#D4AF37" />
          </linearGradient>
          <linearGradient id="gc-area" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#D4AF37" stopOpacity="0.28" />
            <stop offset="1" stopColor="#D4AF37" stopOpacity="0" />
          </linearGradient>
        </defs>
        <motion.path
          d={area}
          fill="url(#gc-area)"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 1, duration: 1.2 }}
        />
        <motion.path
          d={line}
          fill="none"
          stroke="url(#gc-line)"
          strokeWidth="3"
          strokeLinecap="round"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 2.2, ease: "easeInOut" }}
        />
        <circle cx={pts[0][0]} cy={pts[0][1]} r="5" fill="#0B0A07" stroke="#F0D68A" strokeWidth="2.5" />
        <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="6" fill="#D4AF37" stroke="#0B0A07" strokeWidth="2" />
        <text x={pts[0][0]} y={pts[0][1] - 12} fill="#9C927C" fontSize="15">{fmt(first.value)}</text>
        <text x={pts[pts.length - 1][0]} y={pts[pts.length - 1][1] - 14} textAnchor="end" fill="#F0D68A" fontSize="16" fontWeight="600">{fmt(last.value)}</text>
        {idxs.map((i) => (
          <text
            key={points[i].year}
            x={pts[i][0]}
            y={H - 10}
            textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"}
            fill="#9C927C"
            fontSize="13"
          >
            {points[i].year}
          </text>
        ))}
      </svg>
    </div>
  );
}
